import { useMemo } from 'react'

import type { ToolbarSmsListItem } from '#/shared/api/generated/shipment/models'
import { useListItemsApiV1ToolbarTypeGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { formatNoteTimestamp, formatUsPhone } from '#/shared/lib/format'

const DEFAULT_SMS_FEED_SIZE = 100

export interface ShipmentSmsFeedItem {
  id: string
  direction: string
  status: string
  body: string
  fromPhone: string
  toPhones: string[]
  fromPhoneDisplay: string
  toPhonesDisplay: string
  sentAt: string | undefined
  deliveredAt: string | undefined
  timestamp: string
  occurredAt: string
}

interface UseShipmentSmsFeedOptions {
  enabled?: boolean
  size?: number
}

export interface UseShipmentSmsFeedResult {
  messages: ShipmentSmsFeedItem[]
  isLoading: boolean
}

export function toShipmentSmsFeedItem(
  sms: ToolbarSmsListItem,
): ShipmentSmsFeedItem {
  const fromPhone = sms.from_phone ?? ''
  const toPhones = sms.to_phones ?? []
  const created = sms.created_at ?? sms.sent_at ?? ''
  return {
    id: sms.id,
    direction: sms.direction,
    status: sms.status,
    body: sms.body ?? '',
    fromPhone,
    toPhones,
    fromPhoneDisplay: formatUsPhone(fromPhone),
    toPhonesDisplay: toPhones.map(formatUsPhone).filter(Boolean).join(', '),
    sentAt: sms.sent_at ?? undefined,
    deliveredAt: sms.delivered_at ?? undefined,
    timestamp: created ? formatNoteTimestamp(created) : '',
    occurredAt: created,
  }
}

export function useShipmentSmsFeed(
  shipmentId: string,
  {
    enabled = true,
    size = DEFAULT_SMS_FEED_SIZE,
  }: UseShipmentSmsFeedOptions = {},
): UseShipmentSmsFeedResult {
  const query = useListItemsApiV1ToolbarTypeGet(
    'sms',
    { shipment_id: shipmentId, size },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  const messages = useMemo<ShipmentSmsFeedItem[]>(() => {
    const items = (query.data?.data.items ??
      []) as unknown as ToolbarSmsListItem[]
    return items.map(toShipmentSmsFeedItem)
  }, [query.data])

  return {
    messages,
    isLoading: query.isLoading && enabled && Boolean(shipmentId),
  }
}
